/**
 * PR-RUNTIME-FINAL — Default capability activation registry.
 *
 * All capabilities default to `disabled` and `productionEnabled: false`.
 * Staging or production enablement is strictly an owner/operator decision and
 * must be recorded in the `capability_activation_state` table, not in source.
 *
 * The default registry is deterministic: `checkedAt` and `checkedSha` are
 * empty strings and are intended to be filled by the build/runtime loader.
 */
export const CAPABILITY_ACTIVATION_REGISTRY_VERSION = "pr-runtime-final.v1";
function entry(capabilityId, overrides) {
    const defaults = {
        capabilityId,
        version: "0.1.0",
        name: capabilityId,
        canonicalModule: `packages/server/src/dispatchers/${capabilityId}Dispatch.ts`,
        dependsOn: [],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        executionMode: "disabled",
        scope: { owner: true, tenant: true, project: true },
        requiredRole: "owner",
        riskClass: "medium",
        approval: { required: true, expirySeconds: 300, singleUse: false },
        idempotency: {
            enabled: true,
            identityFields: ["actorId", "tenantId", "projectId", "jobId", "nonce"],
            ttlSeconds: 3600,
        },
        limits: { concurrency: 1, timeoutMs: 15000, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        audit: {
            eventType: `${capabilityId}_execution`,
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "timestamp", "result"],
            piiFields: ["email", "phone", "address"],
        },
        rollback: { compensatingAction: `revert_${capabilityId}`, safeWindowSeconds: 300, requiresApproval: true },
    };
    const base = { ...defaults, ...overrides };
    return base;
}
function role(role) {
    return role;
}
const CEO_PRIVILEGED_ROLES = [
    "super-admin",
    "super-sub-admin",
];
const CONTROLLED_WRITE_APPS = [
    "smart_print",
    "smart_salary",
    "drishti_kundli",
    "anshika_api",
    "smart_ordering_core",
    "anoopam_press_website",
    "anshika_website_maker",
];
const CONTROLLED_WRITE_ENTRIES = CONTROLLED_WRITE_APPS.map((appId) => entry(`${appId}_controlled_write`, {
    name: `${appId} controlled write`,
    canonicalModule: "packages/server/src/dispatchers/controlledWriteDispatch.ts",
    allowedEnvironment: "disabled",
    productionEnabled: false,
    riskClass: "medium",
    requiredRole: role("super-sub-admin"),
    ceoAllowedRoles: CEO_PRIVILEGED_ROLES,
    approval: { required: true, expirySeconds: 300, singleUse: true },
    limits: {
        concurrency: 1,
        timeoutMs: 15000,
        retryPolicy: { maxAttempts: 1, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 },
    },
    audit: {
        eventType: `${appId}_controlled_write`,
        requiredFields: ["capabilityId", "actorId", "ownerId", "tenantId", "projectId", "requestId", "result"],
        piiFields: ["payload", "secret", "token", "customerContent"],
    },
    rollback: {
        compensatingAction: `${appId}_controlled_write_compensation`,
        safeWindowSeconds: 300,
        requiresApproval: true,
    },
}));
const ENTRIES = [
    entry("chat-gateway", {
        name: "Gateway chat/provider execution",
        canonicalModule: "packages/server/src/chat/chatService.ts",
        dependsOn: [],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "high",
        requiredRole: role("user"),
        ceoAllowedRoles: CEO_PRIVILEGED_ROLES,
        scope: { owner: true, tenant: false, project: true },
        approval: { required: false, expirySeconds: 300, singleUse: false },
        limits: { concurrency: 8, timeoutMs: 60000, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        audit: {
            eventType: "gateway_chat_execution",
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "requestId", "gatewayProfile", "timestamp", "result"],
            piiFields: ["prompt", "systemPrompt"],
        },
        rollback: { compensatingAction: "none_autonomous_conversation_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("coding_agent_coder", {
        name: "Isolated coding proposal agent",
        canonicalModule: "packages/server/src/codingWorkflow/codingWorkflowService.ts",
        allowedEnvironment: "disabled",
        productionEnabled: false,
        executionMode: "disabled",
        requiredRole: role("user"),
        ceoAllowedRoles: CEO_PRIVILEGED_ROLES,
        riskClass: "high",
        scope: { owner: true, tenant: false, project: true },
        approval: { required: false, expirySeconds: 300, singleUse: false },
        audit: {
            eventType: "coding_agent_coder_execution",
            requiredFields: ["capabilityId", "actorId", "ownerId", "tenantId", "projectId", "requestId", "result"],
            piiFields: ["task", "context", "fileContent"],
        },
        rollback: { compensatingAction: "discard_isolated_workspace", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("coding_agent_reviewer", {
        name: "Isolated coding review agent",
        canonicalModule: "packages/server/src/codingWorkflow/codingWorkflowService.ts",
        allowedEnvironment: "disabled",
        productionEnabled: false,
        executionMode: "disabled",
        requiredRole: role("user"),
        riskClass: "medium",
        approval: { required: false, expirySeconds: 300, singleUse: false },
        audit: {
            eventType: "coding_agent_reviewer_execution",
            requiredFields: ["capabilityId", "actorId", "ownerId", "tenantId", "projectId", "requestId", "result"],
            piiFields: ["changes"],
        },
        rollback: { compensatingAction: "none_read_only_review", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("coding_agent_apply", {
        name: "Approved isolated coding apply",
        canonicalModule: "packages/server/src/codingWorkflow/codingWorkflowService.ts",
        allowedEnvironment: "disabled",
        productionEnabled: false,
        executionMode: "disabled",
        requiredRole: role("owner"),
        riskClass: "high",
        approval: { required: true, expirySeconds: 300, singleUse: true },
        audit: {
            eventType: "coding_agent_apply_execution",
            requiredFields: ["capabilityId", "actorId", "ownerId", "tenantId", "projectId", "requestId", "result"],
            piiFields: ["changes", "approvalToken"],
        },
        rollback: { compensatingAction: "rollback_isolated_workspace", safeWindowSeconds: 300, requiresApproval: true },
    }),
    entry("external-app-readonly-chat", {
        name: "Canonical external-app read-only chat (HMAC-v2 server-to-server)",
        canonicalModule: "packages/server/src/dispatchers/externalAppChatDispatch.ts",
        dependsOn: ["chat-gateway"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "medium",
        requiredRole: role("user"),
        limits: { concurrency: 4, timeoutMs: 30000, retryPolicy: { maxAttempts: 2, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 10000 } },
        audit: {
            eventType: "external_app_readonly_chat_execution",
            requiredFields: ["capabilityId", "actorId", "appId", "ownerId", "projectId", "requestId", "timestamp", "result"],
            piiFields: ["message"],
        },
        rollback: { compensatingAction: "none_readonly_chat_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("external-app-coding", {
        name: "Canonical external-app code generation (HMAC-v2 server-to-server)",
        canonicalModule: "packages/server/src/dispatchers/externalAppCodeDispatch.ts",
        dependsOn: ["chat-gateway", "coding_agent_coder"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "high",
        requiredRole: role("user"),
        limits: { concurrency: 2, timeoutMs: 120000, retryPolicy: { maxAttempts: 1, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 10000 } },
        audit: {
            eventType: "external_app_coding_execution",
            requiredFields: ["capabilityId", "actorId", "appId", "ownerId", "projectId", "requestId", "timestamp", "result"],
            piiFields: ["prompt"],
        },
        rollback: { compensatingAction: "none_plan_only_no_apply", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("external-app-status", {
        name: "Canonical external-app status/health (HMAC-v2 server-to-server)",
        canonicalModule: "packages/server/src/dispatchers/externalAppCodeDispatch.ts",
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "low",
        requiredRole: role("user"),
        limits: { concurrency: 8, timeoutMs: 10000, retryPolicy: { maxAttempts: 1, initialDelayMs: 500, backoffMultiplier: 2, maxDelayMs: 2000 } },
        audit: {
            eventType: "external_app_status_execution",
            requiredFields: ["capabilityId", "actorId", "appId", "ownerId", "projectId", "requestId", "timestamp", "result"],
            piiFields: [],
        },
        rollback: { compensatingAction: "none_readonly_status_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("external-app-project-read", {
        name: "Canonical external-app project builder read ops (HMAC-v2 server-to-server)",
        canonicalModule: "packages/server/src/dispatchers/externalAppCodeDispatch.ts",
        dependsOn: ["chat-gateway"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "low",
        requiredRole: role("user"),
        limits: { concurrency: 8, timeoutMs: 15000, retryPolicy: { maxAttempts: 1, initialDelayMs: 500, backoffMultiplier: 2, maxDelayMs: 2000 } },
        audit: {
            eventType: "external_app_project_read_execution",
            requiredFields: ["capabilityId", "actorId", "appId", "ownerId", "projectId", "requestId", "timestamp", "result"],
            piiFields: [],
        },
        rollback: { compensatingAction: "none_readonly_project_reads_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("external-app-project-write", {
        name: "Canonical external-app project builder write ops (HMAC-v2 server-to-server)",
        canonicalModule: "packages/server/src/dispatchers/externalAppCodeDispatch.ts",
        dependsOn: ["chat-gateway"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "high",
        requiredRole: role("super-sub-admin"),
        limits: { concurrency: 2, timeoutMs: 60000, retryPolicy: { maxAttempts: 1, initialDelayMs: 500, backoffMultiplier: 2, maxDelayMs: 2000 } },
        audit: {
            eventType: "external_app_project_write_execution",
            requiredFields: ["capabilityId", "actorId", "appId", "ownerId", "projectId", "requestId", "timestamp", "result"],
            piiFields: [],
        },
        rollback: { compensatingAction: "snapshot_restore_available", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("external-app-secret-vault", {
        name: "Canonical external-app secret vault ops (HMAC-v2 server-to-server)",
        canonicalModule: "packages/server/src/dispatchers/externalAppSecretsDispatch.ts",
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "high",
        requiredRole: role("super-sub-admin"),
        limits: { concurrency: 2, timeoutMs: 30000, retryPolicy: { maxAttempts: 1, initialDelayMs: 500, backoffMultiplier: 2, maxDelayMs: 2000 } },
        audit: {
            eventType: "external_app_secret_vault_execution",
            requiredFields: ["capabilityId", "actorId", "appId", "ownerId", "projectId", "requestId", "timestamp", "result"],
            piiFields: ["value"],
        },
        rollback: { compensatingAction: "none_secret_metadata_ops_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("external-app-agent-plugin", {
        name: "Canonical external-app agent plugin install/manage (HMAC-v2 server-to-server)",
        canonicalModule: "packages/server/src/dispatchers/externalAppAgentPluginDispatch.ts",
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "high",
        requiredRole: role("super-sub-admin"),
        limits: { concurrency: 2, timeoutMs: 60000, retryPolicy: { maxAttempts: 1, initialDelayMs: 500, backoffMultiplier: 2, maxDelayMs: 2000 } },
        audit: {
            eventType: "external_app_agent_plugin_execution",
            requiredFields: ["capabilityId", "actorId", "appId", "ownerId", "projectId", "requestId", "timestamp", "result"],
            piiFields: ["manifest", "installIntent"],
        },
        rollback: { compensatingAction: "uninstall_plugin_available", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("support-ticket-user", {
        name: "Canonical help desk customer ops (tickets, replies, feedback)",
        canonicalModule: "packages/server/src/dispatchers/supportDispatch.ts",
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "low",
        requiredRole: role("user"),
        limits: { concurrency: 8, timeoutMs: 15000, retryPolicy: { maxAttempts: 1, initialDelayMs: 500, backoffMultiplier: 2, maxDelayMs: 2000 } },
        audit: {
            eventType: "support_ticket_user_execution",
            requiredFields: ["capabilityId", "actorId", "ownerId", "ticketId", "requestId", "timestamp", "result"],
            piiFields: [],
        },
        rollback: { compensatingAction: "none_support_ticket_records_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("support-staff", {
        name: "Canonical help desk staff ops (inbox, assign, reply, notes, resolve)",
        canonicalModule: "packages/server/src/dispatchers/supportDispatch.ts",
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "medium",
        requiredRole: role("super-sub-admin"),
        limits: { concurrency: 8, timeoutMs: 15000, retryPolicy: { maxAttempts: 1, initialDelayMs: 500, backoffMultiplier: 2, maxDelayMs: 2000 } },
        audit: {
            eventType: "support_staff_execution",
            requiredFields: ["capabilityId", "actorId", "ownerId", "ticketId", "requestId", "timestamp", "result"],
            piiFields: [],
        },
        rollback: { compensatingAction: "support_events_audit_trail", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("support-manager", {
        name: "Canonical help desk manager ops (SLA policy, closed-ticket reopen)",
        canonicalModule: "packages/server/src/dispatchers/supportDispatch.ts",
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "high",
        requiredRole: role("super-admin"),
        limits: { concurrency: 4, timeoutMs: 15000, retryPolicy: { maxAttempts: 1, initialDelayMs: 500, backoffMultiplier: 2, maxDelayMs: 2000 } },
        audit: {
            eventType: "support_manager_execution",
            requiredFields: ["capabilityId", "actorId", "ownerId", "requestId", "timestamp", "result"],
            piiFields: [],
        },
        rollback: { compensatingAction: "support_events_audit_trail", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("external-app-support-read", {
        name: "Canonical external-app support read ops (HMAC-v2 server-to-server)",
        canonicalModule: "packages/server/src/dispatchers/externalAppCodeDispatch.ts",
        dependsOn: ["chat-gateway"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "low",
        requiredRole: role("user"),
        limits: { concurrency: 8, timeoutMs: 15000, retryPolicy: { maxAttempts: 1, initialDelayMs: 500, backoffMultiplier: 2, maxDelayMs: 2000 } },
        audit: {
            eventType: "external_app_support_read_execution",
            requiredFields: ["capabilityId", "actorId", "appId", "ownerId", "requestId", "timestamp", "result"],
            piiFields: [],
        },
        rollback: { compensatingAction: "none_readonly_support_reads_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("external-app-support-write", {
        name: "Canonical external-app support write ops (HMAC-v2 server-to-server)",
        canonicalModule: "packages/server/src/dispatchers/externalAppCodeDispatch.ts",
        dependsOn: ["chat-gateway"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "medium",
        requiredRole: role("user"),
        limits: { concurrency: 4, timeoutMs: 30000, retryPolicy: { maxAttempts: 1, initialDelayMs: 500, backoffMultiplier: 2, maxDelayMs: 2000 } },
        audit: {
            eventType: "external_app_support_write_execution",
            requiredFields: ["capabilityId", "actorId", "appId", "ownerId", "requestId", "timestamp", "result"],
            piiFields: [],
        },
        rollback: { compensatingAction: "support_events_audit_trail", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("knowledge:request", {
        name: "Canonical knowledge request idempotency anchor",
        canonicalModule: "packages/server/src/dispatchers/knowledgeDispatch.ts",
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "low",
        requiredRole: role("user"),
        limits: { concurrency: 4, timeoutMs: 15000, retryPolicy: { maxAttempts: 2, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 10000 } },
        audit: {
            eventType: "knowledge_request_execution",
            requiredFields: ["capabilityId", "actorId", "appId", "ownerId", "projectId", "requestId", "timestamp", "result"],
            piiFields: [],
        },
        rollback: { compensatingAction: "none_readonly_request", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("tool-sandbox", {
        name: "Approved tool/skill sandbox execution",
        canonicalModule: "packages/core/src/extension-platform/skillExecution.ts",
        dependsOn: ["chat-gateway"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "critical",
        requiredRole: role("super-admin"),
        limits: { concurrency: 4, timeoutMs: 30000, cpuMax: 1, memoryMaxMb: 512, pidMax: 64, outputBytesMax: 1048576, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        audit: {
            eventType: "tool_sandbox_execution",
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "command", "sandboxArgs", "timestamp", "result"],
            piiFields: ["command", "stdout", "stderr"],
        },
        rollback: { compensatingAction: "kill_process_group_and_audit", safeWindowSeconds: 5, requiresApproval: false },
    }),
    entry("scheduled-job", {
        name: "Persistent scheduled job/queue execution",
        canonicalModule: "packages/core/src/cwj-task-engine",
        dependsOn: ["tool-sandbox"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "high",
        requiredRole: role("super-sub-admin"),
        limits: { concurrency: 2, timeoutMs: 300000, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        idempotency: { enabled: true, identityFields: ["actorId", "tenantId", "projectId", "jobId"], ttlSeconds: 86400 },
        audit: {
            eventType: "scheduled_job_execution",
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "jobId", "leaseToken", "timestamp", "result"],
            piiFields: [],
        },
        rollback: { compensatingAction: "release_lease_and_dead_letter", safeWindowSeconds: 60, requiresApproval: true },
    }),
    entry("desktop-broker", {
        name: "Desktop/browser broker real action",
        canonicalModule: "packages/server/src/dispatchers/controlDispatch.ts",
        dependsOn: [],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "critical",
        requiredRole: role("super-admin"),
        ceoAllowedRoles: CEO_PRIVILEGED_ROLES,
        scope: { owner: true, tenant: false, project: true },
        limits: { concurrency: 1, timeoutMs: 10000, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        audit: {
            eventType: "desktop_broker_execution",
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "bridgeId", "jarvisLockState", "timestamp", "result"],
            piiFields: ["sessionToken"],
        },
        rollback: { compensatingAction: "revoke_session_and_emergency_stop", safeWindowSeconds: 0, requiresApproval: true },
    }),
    entry("smart-print-estimate", {
        name: "Smart Print estimate adapter",
        canonicalModule: "packages/server/src/app-adapters/smartPrintAdapter.ts",
        dependsOn: ["chat-gateway"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "medium",
        requiredRole: role("super-sub-admin"),
        limits: { concurrency: 4, timeoutMs: 30000, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        audit: {
            eventType: "smart_print_estimate_execution",
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "jobId", "nonce", "requestHash", "timestamp", "result"],
            piiFields: ["customerId"],
        },
        rollback: { compensatingAction: "none_idempotent_estimate", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("smart-print-readonly", {
        name: "Smart Print read-only Core adapter",
        canonicalModule: "packages/server/src/app-adapters/smartPrintReadonlyAdapter.ts",
        dependsOn: ["chat-gateway"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "low",
        requiredRole: role("user"),
        limits: { concurrency: 8, timeoutMs: 30000, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        audit: {
            eventType: "smart_print_readonly_execution",
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "intent", "timestamp", "result"],
            piiFields: ["customerId"],
        },
        rollback: { compensatingAction: "none_read_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("artifact-export", {
        name: "CWJ artifact export and package verification",
        canonicalModule: "packages/core/src/cwj-artifact-export/artifactExporter.ts",
        dependsOn: ["tool-sandbox"],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "medium",
        requiredRole: role("super-sub-admin"),
        limits: { concurrency: 2, timeoutMs: 120000, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        audit: {
            eventType: "artifact_export_execution",
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "artifactPath", "manifestSha", "timestamp", "result"],
            piiFields: ["artifactPath"],
        },
        rollback: { compensatingAction: "remove_exported_artifact_and_manifest", safeWindowSeconds: 300, requiresApproval: true },
    }),
    entry("dify-knowledge", {
        name: "Dify knowledge read-only adapter",
        canonicalModule: "packages/server/src/external-agent-router/difyKnowledgeAdapter.ts",
        dependsOn: [],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "low",
        requiredRole: role("owner"),
        ceoAllowedRoles: CEO_PRIVILEGED_ROLES,
        scope: { owner: true, tenant: false, project: true },
        approval: { required: false, expirySeconds: 300, singleUse: false },
        limits: { concurrency: 8, timeoutMs: 30000, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        audit: {
            eventType: "dify_knowledge_execution",
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "requestId", "action", "timestamp", "result"],
            piiFields: ["query"],
        },
        rollback: { compensatingAction: "none_read_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("hermes-agent", {
        name: "Hermes safe agent adapter",
        canonicalModule: "packages/server/src/external-agent-router/hermesAgentAdapter.ts",
        dependsOn: [],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "medium",
        requiredRole: role("owner"),
        ceoAllowedRoles: CEO_PRIVILEGED_ROLES,
        scope: { owner: true, tenant: false, project: true },
        approval: { required: false, expirySeconds: 300, singleUse: false },
        limits: { concurrency: 4, timeoutMs: 30000, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        audit: {
            eventType: "hermes_agent_execution",
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "requestId", "action", "timestamp", "result"],
            piiFields: ["message", "query"],
        },
        rollback: { compensatingAction: "none_read_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    entry("goose-execution", {
        name: "Goose read-only/planning adapter",
        canonicalModule: "packages/server/src/external-agent-router/gooseExecutionAdapter.ts",
        dependsOn: [],
        allowedEnvironment: "disabled",
        productionEnabled: false,
        riskClass: "medium",
        requiredRole: role("owner"),
        ceoAllowedRoles: CEO_PRIVILEGED_ROLES,
        scope: { owner: true, tenant: false, project: true },
        approval: { required: false, expirySeconds: 300, singleUse: false },
        limits: { concurrency: 4, timeoutMs: 30000, retryPolicy: { maxAttempts: 3, initialDelayMs: 1000, backoffMultiplier: 2, maxDelayMs: 60000 } },
        audit: {
            eventType: "goose_execution_plan",
            requiredFields: ["capabilityId", "actorId", "tenantId", "projectId", "requestId", "action", "timestamp", "result"],
            piiFields: ["query"],
        },
        rollback: { compensatingAction: "none_read_only", safeWindowSeconds: 0, requiresApproval: false },
    }),
    ...CONTROLLED_WRITE_ENTRIES,
];
export const DEFAULT_CAPABILITY_ACTIVATION_REGISTRY = Object.freeze({
    version: CAPABILITY_ACTIVATION_REGISTRY_VERSION,
    checkedAt: "",
    checkedSha: "",
    entries: Object.freeze(ENTRIES),
});
export function getCapabilityActivationEntry(registry, capabilityId) {
    return registry.entries.find((e) => e.capabilityId === capabilityId);
}
function isSemver(value) {
    return /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-[a-zA-Z0-9.-]+)?(?:\+[a-zA-Z0-9.-]+)?$/.test(value);
}
function isValidEnvironment(value) {
    return value === "disabled" || value === "staging" || value === "production";
}
export function isExecutionMode(value) {
    return value === "disabled" || value === "plan_only" || value === "approval_required" || value === "live";
}
function isValidRole(value) {
    return value === "super-admin" || value === "super-sub-admin" || value === "user" || value === "owner";
}
function isValidRiskClass(value) {
    return value === "low" || value === "medium" || value === "high" || value === "critical";
}
function isPositiveInteger(value) {
    return Number.isInteger(value) && value > 0;
}
function isNonNegativeInteger(value) {
    return Number.isInteger(value) && value >= 0;
}
/**
 * Validate a registry for internal consistency.  Returns all errors so that
 * callers can fail-closed without guessing.
 */
export function validateCapabilityActivationRegistry(registry) {
    const errors = [];
    const ids = new Set();
    if (!registry.version || typeof registry.version !== "string") {
        errors.push("registry.version must be a non-empty string");
    }
    if (typeof registry.checkedAt !== "string") {
        errors.push("registry.checkedAt must be a string");
    }
    if (typeof registry.checkedSha !== "string") {
        errors.push("registry.checkedSha must be a string");
    }
    if (!Array.isArray(registry.entries)) {
        errors.push("registry.entries must be an array");
        return { valid: false, errors };
    }
    for (const entry of registry.entries) {
        if (!entry.capabilityId || typeof entry.capabilityId !== "string") {
            errors.push("entry.capabilityId must be a non-empty string");
            continue;
        }
        if (ids.has(entry.capabilityId)) {
            errors.push(`duplicate capabilityId: ${entry.capabilityId}`);
        }
        else {
            ids.add(entry.capabilityId);
        }
        if (!isSemver(entry.version)) {
            errors.push(`entry ${entry.capabilityId}: version must be semver`);
        }
        if (!entry.name || typeof entry.name !== "string") {
            errors.push(`entry ${entry.capabilityId}: name must be a non-empty string`);
        }
        if (!entry.canonicalModule || typeof entry.canonicalModule !== "string") {
            errors.push(`entry ${entry.capabilityId}: canonicalModule must be a non-empty string`);
        }
        if (!isValidEnvironment(entry.allowedEnvironment)) {
            errors.push(`entry ${entry.capabilityId}: allowedEnvironment must be disabled, staging or production`);
        }
        if (!isExecutionMode(entry.executionMode)) {
            errors.push(`entry ${entry.capabilityId}: executionMode must be disabled, plan_only, approval_required or live`);
        }
        if (typeof entry.productionEnabled !== "boolean") {
            errors.push(`entry ${entry.capabilityId}: productionEnabled must be boolean`);
        }
        if (!isValidRole(entry.requiredRole)) {
            errors.push(`entry ${entry.capabilityId}: requiredRole must be super-admin, super-sub-admin, user or owner`);
        }
        if (entry.ceoAllowedRoles !== undefined &&
            (entry.ceoAllowedRoles.length === 0 ||
                new Set(entry.ceoAllowedRoles).size !== entry.ceoAllowedRoles.length ||
                entry.ceoAllowedRoles.some((allowedRole) => allowedRole !== "super-admin" && allowedRole !== "super-sub-admin"))) {
            errors.push(`entry ${entry.capabilityId}: ceoAllowedRoles must contain unique privileged CEO roles`);
        }
        if (!isValidRiskClass(entry.riskClass)) {
            errors.push(`entry ${entry.capabilityId}: riskClass must be low, medium, high or critical`);
        }
        if (!isPositiveInteger(entry.approval.expirySeconds)) {
            errors.push(`entry ${entry.capabilityId}: approval.expirySeconds must be a positive integer`);
        }
        if (typeof entry.approval.required !== "boolean") {
            errors.push(`entry ${entry.capabilityId}: approval.required must be boolean`);
        }
        if (typeof entry.approval.singleUse !== "boolean") {
            errors.push(`entry ${entry.capabilityId}: approval.singleUse must be boolean`);
        }
        if (!isPositiveInteger(entry.idempotency.ttlSeconds)) {
            errors.push(`entry ${entry.capabilityId}: idempotency.ttlSeconds must be a positive integer`);
        }
        if (!Array.isArray(entry.idempotency.identityFields) || entry.idempotency.identityFields.length === 0) {
            errors.push(`entry ${entry.capabilityId}: idempotency.identityFields must be a non-empty array`);
        }
        if (!isPositiveInteger(entry.limits.concurrency)) {
            errors.push(`entry ${entry.capabilityId}: limits.concurrency must be a positive integer`);
        }
        if (!isPositiveInteger(entry.limits.timeoutMs)) {
            errors.push(`entry ${entry.capabilityId}: limits.timeoutMs must be a positive integer`);
        }
        const rp = entry.limits.retryPolicy;
        if (!rp || typeof rp !== "object" || !isPositiveInteger(rp.maxAttempts) || !isPositiveInteger(rp.initialDelayMs) || typeof rp.backoffMultiplier !== "number" || rp.backoffMultiplier < 1 || !isPositiveInteger(rp.maxDelayMs)) {
            errors.push(`entry ${entry.capabilityId}: limits.retryPolicy must define positive maxAttempts/initialDelayMs/maxDelayMs and backoffMultiplier >= 1`);
        }
        if (entry.limits.cpuMax !== undefined && (typeof entry.limits.cpuMax !== "number" || entry.limits.cpuMax <= 0)) {
            errors.push(`entry ${entry.capabilityId}: limits.cpuMax must be a positive number when provided`);
        }
        if (entry.limits.memoryMaxMb !== undefined && !isPositiveInteger(entry.limits.memoryMaxMb)) {
            errors.push(`entry ${entry.capabilityId}: limits.memoryMaxMb must be a positive integer when provided`);
        }
        if (entry.limits.pidMax !== undefined && !isPositiveInteger(entry.limits.pidMax)) {
            errors.push(`entry ${entry.capabilityId}: limits.pidMax must be a positive integer when provided`);
        }
        if (entry.limits.outputBytesMax !== undefined && !isPositiveInteger(entry.limits.outputBytesMax)) {
            errors.push(`entry ${entry.capabilityId}: limits.outputBytesMax must be a positive integer when provided`);
        }
        if (!entry.audit.eventType || typeof entry.audit.eventType !== "string") {
            errors.push(`entry ${entry.capabilityId}: audit.eventType must be a non-empty string`);
        }
        if (!Array.isArray(entry.audit.requiredFields)) {
            errors.push(`entry ${entry.capabilityId}: audit.requiredFields must be an array`);
        }
        if (!Array.isArray(entry.audit.piiFields)) {
            errors.push(`entry ${entry.capabilityId}: audit.piiFields must be an array`);
        }
        if (typeof entry.rollback.compensatingAction !== "string") {
            errors.push(`entry ${entry.capabilityId}: rollback.compensatingAction must be a string`);
        }
        if (!isNonNegativeInteger(entry.rollback.safeWindowSeconds)) {
            errors.push(`entry ${entry.capabilityId}: rollback.safeWindowSeconds must be a non-negative integer`);
        }
        if (typeof entry.rollback.requiresApproval !== "boolean") {
            errors.push(`entry ${entry.capabilityId}: rollback.requiresApproval must be boolean`);
        }
        if (!Array.isArray(entry.dependsOn)) {
            errors.push(`entry ${entry.capabilityId}: dependsOn must be an array`);
        }
    }
    // Validate dependencies exist and there are no cycles.
    const idSet = new Set(registry.entries.map((e) => e.capabilityId));
    for (const entry of registry.entries) {
        for (const dep of entry.dependsOn) {
            if (!idSet.has(dep)) {
                errors.push(`entry ${entry.capabilityId}: unknown dependency ${dep}`);
            }
        }
    }
    const visiting = new Set();
    const visited = new Set();
    function visit(capabilityId, stack) {
        if (visiting.has(capabilityId)) {
            const cycle = stack.slice(stack.indexOf(capabilityId)).concat(capabilityId).join(" -> ");
            errors.push(`cyclic dependency detected: ${cycle}`);
            return;
        }
        if (visited.has(capabilityId))
            return;
        const entry = getCapabilityActivationEntry(registry, capabilityId);
        if (!entry)
            return;
        visiting.add(capabilityId);
        stack.push(capabilityId);
        for (const dep of entry.dependsOn) {
            visit(dep, stack);
        }
        stack.pop();
        visiting.delete(capabilityId);
        visited.add(capabilityId);
    }
    for (const entry of registry.entries) {
        visit(entry.capabilityId, []);
    }
    return { valid: errors.length === 0, errors };
}
//# sourceMappingURL=registry.js.map